import { Fragment, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  FileSpreadsheet,
  MoreHorizontal,
  Play,
  Plus,
  Shield,
  Trash2,
  Wrench,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  centsToNumber,
  exportToXlsx,
  isoToBrDate,
} from '@/lib/excel-export';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { extractErrorMessage } from '@/lib/api-client';
import { formatDate } from '@/lib/format';
import {
  useDeleteMaintenanceOrder,
  useDeleteMaintenancePlan,
  useMaintenanceOrders,
  useMaintenancePlans,
  useOrderAction,
} from './api';
import {
  ITEM_TYPE_LABELS,
  PRIORITY_LABELS,
  PRIORITY_VARIANT,
  STATUS_LABELS,
  STATUS_VARIANT,
  TYPE_LABELS,
  type MaintenanceOrder,
} from './types';
import { centsToReais } from '@/features/fueling/types';
import { MaintenanceOrderFormDialog } from './maintenance-order-form-dialog';
import { MaintenancePlanFormDialog } from './maintenance-plan-form-dialog';

export function MaintenancePage() {
  const [activeTab, setActiveTab] = useState<'orders' | 'plans'>('orders');
  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [deleteOrderTarget, setDeleteOrderTarget] = useState<MaintenanceOrder | null>(null);

  const plansQuery = useMaintenancePlans({ page: 1, limit: 50 });
  const ordersQuery = useMaintenanceOrders({ page: 1, limit: 50 });
  const deleteOrder = useDeleteMaintenanceOrder();
  const deletePlan = useDeleteMaintenancePlan();
  const orderAction = useOrderAction();

  const plans = plansQuery.data?.data ?? [];
  const orders = ordersQuery.data?.data ?? [];

  const handleExportOrdersXlsx = () => {
    if (orders.length === 0) {
      toast.error('Nenhuma OS para exportar.');
      return;
    }
    const rows = orders.map((o) => ({
      OS: o.orderNumber ?? o.id.slice(0, 8),
      Veículo: o.vehicle?.plate ?? '',
      Marca: o.vehicle?.brand ?? '',
      Modelo: o.vehicle?.model ?? '',
      Tipo: TYPE_LABELS[o.type] ?? o.type,
      Fornecedor: o.supplier?.name ?? '',
      'Tel. Fornecedor': o.supplier?.phone ?? '',
      Prioridade: PRIORITY_LABELS[o.priority] ?? o.priority,
      Status: STATUS_LABELS[o.status] ?? o.status,
      'Custo Peças': centsToNumber(o.partsCostCents),
      'Custo Mão de obra': centsToNumber(o.laborCostCents),
      'Custo Total': centsToNumber(o.totalCostCents),
      Agendada: isoToBrDate(o.scheduledAt),
      Iniciada: isoToBrDate(o.startedAt),
      Concluída: isoToBrDate(o.completedAt),
      Descrição: o.description ?? '',
    }));
    exportToXlsx(rows, {
      filename: 'frota-os',
      sheetName: 'Ordens de Serviço',
      columnWidths: [12, 10, 12, 16, 14, 20, 16, 12, 14, 12, 14, 14, 12, 12, 12, 30],
    });
    toast.success(`${orders.length} OS exportadas`);
  };

  const handleExportPlansXlsx = () => {
    if (plans.length === 0) {
      toast.error('Nenhum plano para exportar.');
      return;
    }
    const rows = plans.map((p) => ({
      Nome: p.name,
      Tipo: TYPE_LABELS[p.type] ?? p.type,
      Veículo: p.vehicle?.plate ?? '',
      'Intervalo (km)': p.intervalKm ?? '',
      'Intervalo (dias)': p.intervalDays ?? '',
      'Próxima Data': isoToBrDate(p.nextDueAt),
      'Próximo km': p.nextDueKm != null ? Math.round(p.nextDueKm / 1000) : '',
      'Última Execução': isoToBrDate(p.lastExecutedAt),
      'Última km': p.lastExecutedKm != null ? Math.round(p.lastExecutedKm / 1000) : '',
      Ativo: p.isActive ? 'Sim' : 'Não',
    }));
    exportToXlsx(rows, {
      filename: 'frota-planos-manutencao',
      sheetName: 'Planos',
      columnWidths: [24, 14, 10, 14, 16, 14, 14, 16, 14, 8],
    });
    toast.success(`${plans.length} planos exportados`);
  };

  const handleAction = async (id: string, action: 'approve' | 'start' | 'complete' | 'cancel') => {
    try {
      const updated = await orderAction.mutateAsync({ id, action });
      const actionLabel =
        action === 'approve'
          ? 'aprovada'
          : action === 'start'
            ? 'iniciada'
            : action === 'complete'
              ? 'concluída'
              : 'cancelada';
      // Toast com info extra quando concluir e tiver plano vinculado
      if (action === 'complete' && updated.planId) {
        toast.success('OS concluída', {
          description:
            'Próxima manutenção do plano vinculado foi recalculada automaticamente. Veja na aba "Planos".',
          duration: 5000,
        });
      } else {
        toast.success(`OS ${actionLabel}`);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Wrench className="size-3.5" /> Operação
          </div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Manutenção</h1>
          <p className="text-sm text-muted-foreground">Planos preventivos e ordens de serviço.</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'orders' ? (
            <>
              <Button variant="outline" onClick={handleExportOrdersXlsx}>
                <FileSpreadsheet className="size-4" />
                Exportar Excel
              </Button>
              <Button onClick={() => setCreateOrderOpen(true)}>
                <Plus className="size-4" />
                Nova OS
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleExportPlansXlsx}>
                <FileSpreadsheet className="size-4" />
                Exportar Excel
              </Button>
              <Button onClick={() => setCreatePlanOpen(true)}>
                <Plus className="size-4" />
                Novo plano
              </Button>
            </>
          )}
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'orders' | 'plans')}>
        <TabsList>
          <TabsTrigger value="orders"><Wrench className="size-4" /> Ordens de Serviço ({orders.length})</TabsTrigger>
          <TabsTrigger value="plans"><Calendar className="size-4" /> Planos ({plans.length})</TabsTrigger>
        </TabsList>

        {/* Tab: OS */}
        <TabsContent value="orders">
          <Card>
            <CardContent className="p-0">
              {ordersQuery.isLoading ? (
                <div className="space-y-3 p-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
              ) : orders.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <Wrench className="size-10 text-muted-foreground" />
                  <p className="text-sm font-medium">Nenhuma OS cadastrada</p>
                  <p className="text-xs text-muted-foreground">
                    Clique em "Nova OS" para registrar a primeira ordem de serviço.
                  </p>
                  <Button onClick={() => setCreateOrderOpen(true)} className="mt-2">
                    <Plus className="size-4" />
                    Nova OS
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-3 font-medium">OS</th>
                        <th className="px-4 py-3 font-medium">Veículo</th>
                        <th className="px-4 py-3 font-medium">Tipo</th>
                        <th className="px-4 py-3 font-medium">Fornecedor</th>
                        <th className="px-4 py-3 font-medium">Prioridade</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium text-right">Custo</th>
                        <th className="px-4 py-3 font-medium">Agendada</th>
                        <th className="px-4 py-3 font-medium">Executada</th>
                        <th className="w-10 px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => {
                        // O que foi feito: a descricao da OS e os itens lancados,
                        // cada um com o seu tipo (Peca, Borracharia, Pneu...).
                        const feito = o.description?.trim() ?? '';
                        const temDetalhe = feito.length > 0 || o.items.length > 0;
                        return (
                        <Fragment key={o.id}>
                        <tr className="border-t border-border transition-colors first:border-t-0 hover:bg-muted/30">
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs">{o.orderNumber ?? o.id.slice(0, 8)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs font-semibold">{o.vehicle?.plate ?? '—'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="secondary">{TYPE_LABELS[o.type] ?? o.type}</Badge>
                          </td>
                          <td className="px-4 py-3 max-w-[180px]">
                            {o.supplier ? (
                              <div>
                                <p className="truncate text-xs font-medium">
                                  {o.supplier.name}
                                </p>
                                {o.supplier.phone && (
                                  <p className="truncate text-[11px] text-muted-foreground">
                                    {o.supplier.phone}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={PRIORITY_VARIANT[o.priority]}>{PRIORITY_LABELS[o.priority]}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={STATUS_VARIANT[o.status]}>{STATUS_LABELS[o.status]}</Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs tabular-nums">
                            {o.totalCostCents > 0 ? centsToReais(o.totalCostCents) : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {o.scheduledAt ? formatDate(o.scheduledAt) : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {o.completedAt ? formatDate(o.completedAt) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="size-8">
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {o.status === 'draft' && (
                                  <DropdownMenuItem onClick={() => handleAction(o.id, 'approve')}>
                                    <Shield className="size-4" /> Aprovar
                                  </DropdownMenuItem>
                                )}
                                {o.status === 'approved' && (
                                  <DropdownMenuItem onClick={() => handleAction(o.id, 'start')}>
                                    <Play className="size-4" /> Iniciar
                                  </DropdownMenuItem>
                                )}
                                {o.status === 'in_progress' && (
                                  <DropdownMenuItem onClick={() => handleAction(o.id, 'complete')}>
                                    <CheckCircle className="size-4" /> Concluir
                                  </DropdownMenuItem>
                                )}
                                {(o.status === 'draft' || o.status === 'approved') && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleAction(o.id, 'cancel')} className="text-warning-foreground">
                                      <XCircle className="size-4" /> Cancelar
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setDeleteOrderTarget(o)} className="text-destructive">
                                  <Trash2 className="size-4" /> Remover
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                        {temDetalhe && (
                          <tr className="transition-colors hover:bg-muted/30">
                            <td colSpan={10} className="px-4 pb-3 pt-0 align-top text-xs text-muted-foreground">
                              {feito && (
                                <p>
                                  <span className="font-medium text-foreground">Serviço: </span>
                                  {feito}
                                </p>
                              )}
                              {o.items.length > 0 && (
                                <ul className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                                  {o.items.map((it) => (
                                    <li key={it.id} className="flex items-center gap-1.5">
                                      <Badge
                                        variant="outline"
                                        className="px-1.5 py-0 text-[10px] font-normal"
                                      >
                                        {ITEM_TYPE_LABELS[it.type] ?? it.type}
                                      </Badge>
                                      <span>{it.description}</span>
                                      {it.odometerMeters != null && (
                                        <span className="font-mono tabular-nums">
                                          {(it.odometerMeters / 1000).toLocaleString('pt-BR')} km
                                        </span>
                                      )}
                                      {it.performedAt && (
                                        <span className="tabular-nums">
                                          {formatDate(it.performedAt)}
                                        </span>
                                      )}
                                      {it.totalCents > 0 && (
                                        <span className="font-mono tabular-nums">
                                          {centsToReais(it.totalCents)}
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                          </tr>
                        )}
                        </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Planos */}
        <TabsContent value="plans">
          {plansQuery.isLoading ? (
            <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
          ) : plans.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <Calendar className="size-10 text-muted-foreground" />
                <p className="text-sm font-medium">Nenhum plano de manutenção</p>
                <p className="text-xs text-muted-foreground">
                  Clique em "Novo plano" para definir manutenções periódicas.
                </p>
                <Button onClick={() => setCreatePlanOpen(true)} className="mt-2">
                  <Plus className="size-4" />
                  Novo plano
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {plans.map((p) => {
                const isOverdue = p.nextDueAt && new Date(p.nextDueAt) < new Date();
                const daysUntilDue = p.nextDueAt
                  ? Math.ceil(
                      (new Date(p.nextDueAt).getTime() - Date.now()) /
                        (24 * 60 * 60 * 1000),
                    )
                  : null;
                const isDueSoon =
                  !isOverdue && daysUntilDue != null && daysUntilDue <= 7;
                return (
                  <Card key={p.id}>
                    <CardContent className="flex items-center justify-between gap-4 p-4">
                      <div className="flex items-center gap-3">
                        <div className={`flex size-11 items-center justify-center rounded-xl ${isOverdue ? 'bg-destructive/10 text-destructive' : isDueSoon ? 'bg-warning/15 text-warning-foreground' : 'bg-primary/10 text-primary'}`}>
                          {isOverdue || isDueSoon ? <AlertTriangle className="size-5" /> : <Calendar className="size-5" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{p.name}</p>
                          <p className="text-xs text-muted-foreground">
                            <Badge variant="secondary" className="mr-1">{TYPE_LABELS[p.type]}</Badge>
                            {p.vehicle && <span className="font-mono">{p.vehicle.plate}</span>}
                            {p.intervalKm && ` · a cada ${(p.intervalKm).toLocaleString('pt-BR')} km`}
                            {p.intervalDays && ` · a cada ${p.intervalDays} dias`}
                          </p>
                          {p.nextDueAt && (
                            <p className="mt-1 text-xs">
                              Próxima:{' '}
                              <span
                                className={
                                  isOverdue
                                    ? 'font-medium text-destructive'
                                    : isDueSoon
                                      ? 'font-medium text-warning-foreground'
                                      : 'text-muted-foreground'
                                }
                              >
                                {formatDate(p.nextDueAt)}
                                {isOverdue && ' (atrasada!)'}
                                {isDueSoon && ` (em ${daysUntilDue} ${daysUntilDue === 1 ? 'dia' : 'dias'})`}
                              </span>
                              {p.nextDueKm != null && (
                                <span className="text-muted-foreground">
                                  {' · ou aos '}
                                  <span className="font-mono">
                                    {(p.nextDueKm / 1000).toLocaleString('pt-BR')} km
                                  </span>
                                </span>
                              )}
                            </p>
                          )}
                          {p.lastExecutedAt && (
                            <p className="mt-0.5 text-[11px] text-muted-foreground">
                              Última execução: {formatDate(p.lastExecutedAt)}
                              {p.lastExecutedKm != null &&
                                ` aos ${(p.lastExecutedKm / 1000).toLocaleString('pt-BR')} km`}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {p.isActive ? <Badge variant="success">Ativo</Badge> : <Badge variant="outline">Inativo</Badge>}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="size-8">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={async () => {
                                try { await deletePlan.mutateAsync(p.id); toast.success('Plano removido'); }
                                catch (err) { toast.error(extractErrorMessage(err)); }
                              }}
                              className="text-destructive"
                            >
                              <Trash2 className="size-4" /> Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <MaintenanceOrderFormDialog open={createOrderOpen} onOpenChange={setCreateOrderOpen} />
      <MaintenancePlanFormDialog open={createPlanOpen} onOpenChange={setCreatePlanOpen} />

      <Dialog open={!!deleteOrderTarget} onOpenChange={(o) => !o && setDeleteOrderTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remover OS</DialogTitle>
            <DialogDescription>Tem certeza?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOrderTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={async () => {
              if (!deleteOrderTarget) return;
              try { await deleteOrder.mutateAsync(deleteOrderTarget.id); toast.success('OS removida'); setDeleteOrderTarget(null); }
              catch (err) { toast.error(extractErrorMessage(err)); }
            }} loading={deleteOrder.isPending}>Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
